import { useQuery } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { Button } from "@wags/ui/button.tsx";
import { toast } from "@wags/ui/sonner.tsx";
import { cn } from "@wags/ui/ui-utils";
import { hasQuizAnswers } from "@wags/matchmaker/pure.ts";
import { useChromeMatchSearch } from "@/lib/ui/last-match-search";
import { useMediaQuery } from "@wags/tanstack-start/react";
import {
    type ComponentPropsWithoutRef,
    type CSSProperties,
    type ReactNode,
    useSyncExternalStore,
} from "react";
import WagsLogo from "@/assets/icons/wags-logo.svg";
import { UserMenu } from "@/components/shared/UserMenu";
import { useHeaderSignIn } from "./shared/header-interactions-provider";
import {
    GOOGLE_SIGN_IN_START_FAILURE,
    sanitizeAuthRedirectUrl,
    useAuth,
    useAuthActions,
    useGoogleSignInStart,
} from "@/lib/core/auth-protocol";
import { authQueryOptions } from "@/lib/core/auth";
import {
    headerAccountSlot,
    knownAuthenticatedFrom,
} from "@/components/shared/header-account-slot";
import { useScrolledPastTop } from "@wags/tanstack-start/browser";
import {
    getHeaderWordmarkSnapshot,
    getStaticHeaderWordmarkSnapshot,
    HEADER_WORDMARK_LOADING_EXIT_MS,
    subscribeHeaderWordmark,
    subscribeStaticHeaderWordmark,
} from "@/lib/core/header-wordmark-motion";

type HeaderProps = ComponentPropsWithoutRef<"header"> & {
    center?: ReactNode;
    leading?: ReactNode;
    logoSearch?: object;
    hideScrollBorder?: boolean;

    logoClassName?: string;

    accountLinkClassName?: string;

    borderVisible?: boolean;

    signInPrimary?: boolean;

    hideSignIn?: boolean;

    statefulLogo?: boolean;
};

const headerWordmarkStyle: CSSProperties & {
    "--wordmark-loading-exit-duration": string;
} = {
    "--wordmark-loading-exit-duration": `${HEADER_WORDMARK_LOADING_EXIT_MS}ms`,
};

export function HeaderWordmark({
    className,
    compact = false,
    stateful = false,
}: {
    className?: string;
    compact?: boolean;
    stateful?: boolean;
}) {
    const prefersReducedMotion = useMediaQuery(
        "(prefers-reduced-motion: reduce)",
    );
    const motionEnabled = stateful && !prefersReducedMotion;
    const { status } = useSyncExternalStore(
        motionEnabled ? subscribeHeaderWordmark : subscribeStaticHeaderWordmark,
        motionEnabled
            ? getHeaderWordmarkSnapshot
            : getStaticHeaderWordmarkSnapshot,
        getStaticHeaderWordmarkSnapshot,
    );

    return (
        <WagsLogo
            aria-hidden="true"
            data-status={status}
            style={headerWordmarkStyle}
            className={cn(
                "header-wordmark",
                compact && "header-wordmark--compact",
                className,
            )}
        />
    );
}

export function Header({
    className,
    center,
    leading,
    logoSearch,
    hideScrollBorder,
    logoClassName,
    accountLinkClassName,
    borderVisible,
    signInPrimary,
    hideSignIn,
    statefulLogo,
    ...props
}: HeaderProps) {
    const { isLoaded, isSignedIn } = useAuth();
    const authState = useQuery(authQueryOptions());
    const accountSlot = headerAccountSlot({
        sessionPending: !isLoaded,
        sessionSignedIn: isSignedIn,
        knownAuthenticated: knownAuthenticatedFrom(authState.data),
    });
    const onSignIn = useHeaderSignIn();
    const scrolled = useScrolledPastTop();
    const googleSignIn = useGoogleSignInStart({
        onFailure: () => toast.error(GOOGLE_SIGN_IN_START_FAILURE),
    });
    const authRedirectUrl = useRouterState({
        select: (state) => state.location.href,
    });

    return (
        <header
            data-safari-chrome-surface
            {...props}
            className={cn(
                "fixed top-0 left-0 right-0 z-50 w-full bg-maltese-white pt-[calc(env(safe-area-inset-top)+10px)] pb-2.5 md:h-header md:py-0",
                className,
            )}
        >
            {!hideScrollBorder && (
                <span
                    aria-hidden="true"
                    className={cn(
                        "pointer-events-none absolute inset-x-0 bottom-0 h-px bg-doberman-black/8 transition-opacity duration-200 ease-out",
                        (borderVisible ?? scrolled)
                            ? "opacity-100"
                            : "opacity-0",
                    )}
                />
            )}
            <div className="page-container relative grid grid-cols-[1fr_auto_1fr] items-center md:h-full">
                {leading ? (
                    <div className="justify-self-start">{leading}</div>
                ) : (
                    <Link
                        to="/"
                        search={logoSearch ?? {}}
                        aria-label="Wags"
                        className="wags-logo-link justify-self-start"
                    >
                        <HeaderWordmark
                            stateful={statefulLogo}
                            className={cn(
                                "h-11 w-auto text-doberman-black transition-colors duration-200 ease-out md:h-[3.25rem]",
                                logoClassName,
                            )}
                        />
                    </Link>
                )}

                {center ?? <div />}

                <div className="md:hidden" />

                <div className="hidden md:flex flex-row items-center gap-2 justify-self-end">
                    <Link
                        to="/saved"
                        aria-label="Saved dogs"
                        className={cn(
                            "flex h-11 items-center rounded-nested px-3 text-label text-doberman-black/70 outline-none transition-[background-color,color,transform] active:scale-[0.98] active:duration-100 motion-reduce:active:scale-100 [@media(hover:hover)]:hover:bg-doberman-black/[0.04] [@media(hover:hover)]:hover:text-doberman-black focus-visible:ring-2 focus-visible:ring-labrador-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-maltese-white",
                            accountLinkClassName,
                        )}
                    >
                        Saved
                    </Link>
                    {accountSlot === "sign-in" && !hideSignIn ? (
                        <Button
                            variant={signInPrimary ? "default" : "outline"}
                            className={cn(
                                "text-label h-11 w-fit px-4 active:scale-[0.98] motion-reduce:active:scale-100",

                                !signInPrimary &&
                                    "border-black/10 text-doberman-black",
                            )}
                            onClick={() => {
                                onSignIn();
                                void googleSignIn.start(
                                    sanitizeAuthRedirectUrl(authRedirectUrl) ??
                                        "/",
                                );
                            }}
                        >
                            Sign in
                        </Button>
                    ) : null}
                    {accountSlot === "account" ? (
                        <AuthenticatedUserMenu
                            className={accountLinkClassName}
                        />
                    ) : null}
                </div>
            </div>
        </header>
    );
}

function AuthenticatedUserMenu({ className }: { className?: string }) {
    const auth = useAuthActions();
    const quizSearch = useChromeMatchSearch();

    return (
        <UserMenu
            className={className}
            quizSearch={quizSearch}
            hasCompletedQuiz={hasQuizAnswers(quizSearch)}
            onSignOut={() => void auth.signOut({ redirectUrl: "/find" })}
        />
    );
}
